/**
 * Currency Utilities
 *
 * Provides currency formatting functions for the Nudgesmart analytics dashboard.
 * Uses Shopify's moneyFormat string for consistent formatting with the merchant's store.
 */

/**
 * Format a monetary amount using Shopify's money format string
 *
 * @param {number} amount - The amount to format
 * @param {string} moneyFormat - Shopify's money format (e.g., "${{amount}}", "Rs. {{amount}}")
 * @param {Object} options - Formatting options
 * @param {boolean} options.showCents - Whether to show cents (default: true for amounts < 1000)
 * @returns {string} Formatted currency string
 */
export function formatMoney(amount, moneyFormat, options = {}) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return formatMoney(0, moneyFormat, options);
  }

  const { showCents = amount < 1000 } = options;

  // Format the number
  const formattedAmount = showCents
    ? amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(amount).toLocaleString('en-US');

  // If no moneyFormat provided, use default USD
  if (!moneyFormat) {
    return `$${formattedAmount}`;
  }

  // Replace Shopify placeholders with formatted amount
  // Shopify uses: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}}
  return moneyFormat
    .replace(/\{\{amount_no_decimals\}\}/g, Math.round(amount).toLocaleString('en-US'))
    .replace(/\{\{amount_with_comma_separator\}\}/g, formattedAmount.replace('.', ','))
    .replace(/\{\{amount\}\}/g, formattedAmount);
}

/**
 * Format a monetary amount in compact form (K, M, B)
 *
 * @param {number} amount - The amount to format
 * @param {string} currencySymbol - Currency symbol (e.g., "$", "Rs.", "€")
 * @returns {string} Compact formatted string (e.g., "$1.2K", "Rs. 45M")
 */
export function formatMoneyCompact(amount, currencySymbol = '$') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${currencySymbol}0`;
  }

  const absAmount = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  if (absAmount >= 1e9) {
    return `${sign}${currencySymbol}${(absAmount / 1e9).toFixed(1)}B`;
  }
  if (absAmount >= 1e6) {
    return `${sign}${currencySymbol}${(absAmount / 1e6).toFixed(1)}M`;
  }
  if (absAmount >= 1e3) {
    return `${sign}${currencySymbol}${(absAmount / 1e3).toFixed(1)}K`;
  }

  return `${sign}${currencySymbol}${absAmount.toFixed(0)}`;
}

/**
 * Extract the currency symbol from Shopify's money format string
 *
 * @param {string} moneyFormat - Shopify's money format (e.g., "${{amount}}", "Rs. {{amount}}")
 * @returns {string} Currency symbol (e.g., "$", "Rs. ", "€")
 */
export function extractCurrencySymbol(moneyFormat) {
  if (!moneyFormat) {
    return '$';
  }

  // Remove the amount placeholder and trim
  const symbol = moneyFormat
    .replace(/\{\{amount[^}]*\}\}/g, '')
    .trim();

  // If nothing left, return $
  return symbol || '$';
}

/**
 * Get currency format information by ISO code
 *
 * @param {string} currencyCode - ISO 4217 currency code (e.g., "USD", "INR", "EUR")
 * @returns {Object} Currency info with symbol and format
 */
export function getCurrencyByCode(currencyCode) {
  const currencies = {
    USD: { symbol: '$', format: '${{amount}}', locale: 'en-US' },
    EUR: { symbol: '€', format: '€{{amount}}', locale: 'de-DE' },
    GBP: { symbol: '£', format: '£{{amount}}', locale: 'en-GB' },
    INR: { symbol: '₹', format: '₹{{amount}}', locale: 'en-IN' },
    CAD: { symbol: 'CA$', format: '${{amount}} CAD', locale: 'en-CA' },
    AUD: { symbol: 'A$', format: '${{amount}} AUD', locale: 'en-AU' },
    JPY: { symbol: '¥', format: '¥{{amount}}', locale: 'ja-JP' },
    CNY: { symbol: '¥', format: '¥{{amount}}', locale: 'zh-CN' },
    BRL: { symbol: 'R$', format: 'R${{amount}}', locale: 'pt-BR' },
    MXN: { symbol: 'MX$', format: '${{amount}} MXN', locale: 'es-MX' },
    SGD: { symbol: 'S$', format: '${{amount}} SGD', locale: 'en-SG' },
    HKD: { symbol: 'HK$', format: 'HK${{amount}}', locale: 'zh-HK' },
    NZD: { symbol: 'NZ$', format: '${{amount}} NZD', locale: 'en-NZ' },
    SEK: { symbol: 'kr', format: '{{amount}} kr', locale: 'sv-SE' },
    NOK: { symbol: 'kr', format: '{{amount}} kr', locale: 'nb-NO' },
    DKK: { symbol: 'kr', format: '{{amount}} kr', locale: 'da-DK' },
    CHF: { symbol: 'CHF', format: 'CHF {{amount}}', locale: 'de-CH' },
    ZAR: { symbol: 'R', format: 'R{{amount}}', locale: 'en-ZA' },
    AED: { symbol: 'AED', format: 'AED {{amount}}', locale: 'ar-AE' },
    SAR: { symbol: 'SAR', format: 'SAR {{amount}}', locale: 'ar-SA' },
    PLN: { symbol: 'zł', format: '{{amount}} zł', locale: 'pl-PL' },
    THB: { symbol: '฿', format: '฿{{amount}}', locale: 'th-TH' },
    MYR: { symbol: 'RM', format: 'RM{{amount}}', locale: 'ms-MY' },
    PHP: { symbol: '₱', format: '₱{{amount}}', locale: 'en-PH' },
    IDR: { symbol: 'Rp', format: 'Rp{{amount}}', locale: 'id-ID' },
    VND: { symbol: '₫', format: '{{amount}}₫', locale: 'vi-VN' },
    KRW: { symbol: '₩', format: '₩{{amount}}', locale: 'ko-KR' },
    TWD: { symbol: 'NT$', format: 'NT${{amount}}', locale: 'zh-TW' },
    TRY: { symbol: '₺', format: '₺{{amount}}', locale: 'tr-TR' },
    RUB: { symbol: '₽', format: '{{amount}} ₽', locale: 'ru-RU' },
    ILS: { symbol: '₪', format: '₪{{amount}}', locale: 'he-IL' },
    CZK: { symbol: 'Kč', format: '{{amount}} Kč', locale: 'cs-CZ' },
    HUF: { symbol: 'Ft', format: '{{amount}} Ft', locale: 'hu-HU' },
    RON: { symbol: 'lei', format: '{{amount}} lei', locale: 'ro-RO' },
    BGN: { symbol: 'лв', format: '{{amount}} лв', locale: 'bg-BG' },
    HRK: { symbol: 'kn', format: '{{amount}} kn', locale: 'hr-HR' },
    CLP: { symbol: 'CLP$', format: '${{amount}}', locale: 'es-CL' },
    COP: { symbol: 'COP$', format: '${{amount}}', locale: 'es-CO' },
    PEN: { symbol: 'S/', format: 'S/{{amount}}', locale: 'es-PE' },
    ARS: { symbol: 'ARS$', format: '${{amount}}', locale: 'es-AR' },
  };

  return currencies[currencyCode] || currencies.USD;
}

/**
 * Format a percentage value
 *
 * @param {number} value - The percentage value (e.g., 0.125 for 12.5%)
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 1)
 * @param {boolean} options.alreadyPercent - If true, value is already a percentage (e.g., 12.5)
 * @returns {string} Formatted percentage string (e.g., "12.5%")
 */
export function formatPercentage(value, options = {}) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  const { decimals = 1, alreadyPercent = false } = options;
  const percentValue = alreadyPercent ? value : value * 100;

  return `${percentValue.toFixed(decimals)}%`;
}

/**
 * Format a number with appropriate suffix (K, M, B)
 *
 * @param {number} value - The number to format
 * @returns {string} Formatted number string
 */
export function formatNumber(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e9) {
    return `${sign}${(absValue / 1e9).toFixed(1)}B`;
  }
  if (absValue >= 1e6) {
    return `${sign}${(absValue / 1e6).toFixed(1)}M`;
  }
  if (absValue >= 1e3) {
    return `${sign}${(absValue / 1e3).toFixed(1)}K`;
  }

  return `${sign}${absValue.toLocaleString('en-US')}`;
}
